import React, { useContext, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  colors,
  FormHelperText,
  InputAdornment,
  makeStyles,
  MenuItem,
} from "@material-ui/core";
import { FormattedMessage, useIntl } from "react-intl";
import { IoIosCalendar } from "react-icons/io";
import * as Yup from "yup";
import { FormikHelpers, useFormik } from "formik";

import { FormHeader } from "./FormHeader";
import { InputField } from "../../../components/input-field/InputField";
import { ImageChooser } from "../../../components/image-chooser/ImageChooser";
import { InputButton } from "../../../components/input-button/InputButton";
import { InputSelect } from "../../../components/input-select/InputSelect";
import {
  IdentificationTypeIdEnum,
  SignupCniRequestAttributes,
  SignupPassportRequestAttributes,
} from "../../../attributes/user-attributes";
import {
  SignupContext,
  SignupDispatchContext,
  SignupTypeEnum,
} from "../../../context/SignupContext";
import { base64, validateFile } from "../../../common/Hooks";
import { UserService } from "../../../services/UserService";
import { Overlay } from "../../../components/overlay/Overlay";
import { InputDate } from "../../../components/input-date/InputDate";
import moment from "moment";
import {
  FILE_MAX_SIZE,
  FILE_MAX_SIZE_STR,
  YYYY_MM_DD_FORMAT,
} from "../../../settings/constants";
import {
  remoteAuthUserRequest,
  useAuthenticationContext,
} from "../../../context/UserContext";
import { HomeScreen } from "../../home/HomeScreen";

interface IdentificationInfoFormProps {
  backCallback: () => void;
}

export const useStyles = makeStyles(
  (theme) => ({
    header: {
      "& .leftWrapper": {
        border: "1px solid red",
      },
    },

    subHeader: {
      fontWeight: theme.typography.fontWeightLight,
      textAlign: "left",

      "& span": {
        fontSize: 14,
      },

      "& p": {
        fontSize: 13,
        fontStyle: "italic",
        color: colors.grey[700],
      },
    },
  }),
  {
    name: "id-info-style",
    flip: null,
  }
);

export const IdentificationInfoForm = (props: IdentificationInfoFormProps) => {
  // Props
  const { backCallback } = props;

  return (
    <>
      <FormHeader>
        <Box component="div">
          <Box component="span" className="title">
            <FormattedMessage id="sharedScreen.identificationInfo" />
          </Box>
          <Box component="span" className="subTitle">
            <FormattedMessage id="sharedScreen.identificationInfoNotice" />
          </Box>
        </Box>
      </FormHeader>

      <IdentificationInformationForm backCallback={backCallback} />
    </>
  );
};

export const IdentificationInformationForm = (
  props: IdentificationInfoFormProps,
  ...rest: any[]
) => {
  const classes = useStyles(props);
  const intl = useIntl();
  const history = useHistory();
  // Props
  const { backCallback } = props;
  // State
  const [idType, setIdType] = useState(
    (): IdentificationTypeIdEnum => IdentificationTypeIdEnum.cni
  );
  // Context
  const signupState = useContext(SignupContext);
  const signupDispatch = useContext(SignupDispatchContext);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [user, token, authDispatch] = useAuthenticationContext();

  const handleIdType = (event: any, child: any) => {
    setIdType(event.target.value);
    formik.values.identification_type_id = event.target.value;
    formik.setErrors({});

    if (idType === IdentificationTypeIdEnum.cni) {
      formik.values.passport = null;
    } else {
      formik.values.cni_back = null;
      formik.values.cni_front = null;
    }
  };

  const initFormikState = ():
    | SignupCniRequestAttributes
    | SignupPassportRequestAttributes => {
    return signupState.id_info;
  };

  const validateIdFiles = (): any => {
    if (idType === IdentificationTypeIdEnum.cni) {
      return {
        cni_back: Yup.mixed()
          .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
          .test(
            "fileSize",
            intl.formatMessage(
              { id: "error.tooLargePicture" },
              { max: FILE_MAX_SIZE_STR }
            ),
            (value) => {
              return value !== null && validateFile(value, FILE_MAX_SIZE);
            }
          ),
        cni_front: Yup.mixed()
          .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
          .test(
            "fileSize",
            intl.formatMessage(
              { id: "error.tooLargePicture" },
              { max: FILE_MAX_SIZE_STR }
            ),
            (value) => {
              return value !== null && validateFile(value, FILE_MAX_SIZE);
            }
          ),
      };
    }

    return {
      passport: Yup.mixed()
        .required(intl.formatMessage({ id: "error.selectValidPhoto" }))
        .test(
          "fileSize",
          intl.formatMessage(
            { id: "error.tooLargePicture" },
            { max: FILE_MAX_SIZE_STR }
          ),
          (value) => {
            return value !== null && validateFile(value, FILE_MAX_SIZE);
          }
        ),
    };
  };

  const formikValidation = (): any => {
    return Yup.object().shape({
      id_number: Yup.string().required(
        intl.formatMessage({ id: "error.idNumber" })
      ),
      expiration_date: Yup.string().required(
        intl.formatMessage({ id: "error.date" })
      ),
      issue_date: Yup.string().required(
        intl.formatMessage({ id: "error.date" })
      ),
      ...validateIdFiles(),
    });
  };

  const handleImageSelected = (name: string, file: any) => {
    if (file !== null) {
      base64(file, (result) => {
        formik.values[name] = result;
      });
    } else {
      formik.values[name] = null;
    }
    formik.setErrors({ ...formik.errors, [name]: undefined });
  };

  const genericError = () => {
    if (formik.errors.unknown) {
      return formik.errors.unknown as string;
    }

    return "";
  };

  const storeSignupData = () => {
    signupDispatch({
      type: SignupTypeEnum.store,
      payload: {
        id_info: formik.values,
      },
    });
  };

  const handleDateChange = (name: string, dateString: string) => {
    formik.setValues({ ...formik.values, [name]: dateString });
  };

  const formik = useFormik({
    initialValues: initFormikState(),
    validationSchema: formikValidation(),
    onSubmit: async (
      values: SignupCniRequestAttributes | SignupPassportRequestAttributes,
      { setErrors, setSubmitting }: FormikHelpers<any>
    ) => {
      try {
        // Store temp data before send
        storeSignupData();

        let postData = {
          ...values,
          ...signupState.personal,
        };
        const response = await UserService.register(postData);

        if (response.isSuccess) {
          switch (response.data.code) {
            case 100: // Ok
              // Delete temp data
              signupDispatch({
                type: SignupTypeEnum.clear,
              });

              // Get token
              let token = JSON.parse(JSON.stringify(response.data.data));
              // Get user info
              await remoteAuthUserRequest(token.access_token, authDispatch);
              setSubmitting(false);

              history.replace(HomeScreen.routeName);
              break;
            case 150: // Validation error
              setSubmitting(false);
              setErrors(JSON.parse(JSON.stringify(response.data.data)));
              break;
            case 102: // Unable to check your phone number verification
              setSubmitting(false);
              setErrors({ unknown: response.data.message });
              break;
            default:
              // Unknown error
              setSubmitting(false);
              setErrors({ unknown: response.data.message });
              break;
          }
        } else {
          setSubmitting(false);
          setErrors({
            unknown: intl.formatMessage({ id: "error.unknownError" }),
          });
        }
      } catch (err) {
        setSubmitting(false);
        setErrors({
          unknown: intl.formatMessage({ id: "error.unknownError" }),
        });
      }
    },
  });

  return (
    <>
      <Box component="div" mt={6}>
        {formik.errors ? (
          <Box mb={4} mt={4} component="div" className="errorWrapper">
            {genericError()}
          </Box>
        ) : (
          <></>
        )}

        <form noValidate autoComplete="off" onSubmit={formik.handleSubmit}>
          <Box mt={2}>
            <InputSelect
              id="identification_type_id"
              name="identification_type_id"
              fullWidth
              label={intl.formatMessage({
                id: "sharedScreen.idType",
              })}
              onChange={handleIdType}
              value={idType}
              error={
                Boolean(formik.touched.identification_type_id) &&
                Boolean(formik.errors.identification_type_id)
              }
              helperText={
                formik.touched.identification_type_id &&
                formik.errors.identification_type_id
              }
            >
              <MenuItem value="" disabled>
                <FormattedMessage id="sharedScreen.selectIDTypes" />
              </MenuItem>
              <MenuItem value={IdentificationTypeIdEnum.cni}>
                <FormattedMessage id="sharedScreen.nationalIdentityCard" />
              </MenuItem>
              <MenuItem value={IdentificationTypeIdEnum.passport}>
                <FormattedMessage id="sharedScreen.passport" />
              </MenuItem>
            </InputSelect>
          </Box>
          <Box mt={2}>
            <InputField
              id="id_number"
              name="id_number"
              fullWidth
              label={intl.formatMessage({ id: "sharedScreen.idNumber" })}
              placeholder={intl.formatMessage({
                id: "sharedScreen.enterValidIdNumber",
              })}
              type="text"
              value={formik.values.id_number}
              onChange={formik.handleChange}
              error={
                Boolean(formik.touched.id_number) &&
                Boolean(formik.errors.id_number)
              }
              helperText={formik.touched.id_number && formik.errors.id_number}
            />
          </Box>

          <Box
            className="inlineForm"
            display="flex"
            mt={2}
            flexWrap="nowrap"
            alignItems="start"
          >
            <Box className="inlineFormElement">
              <InputDate
                maxDate={moment().format(YYYY_MM_DD_FORMAT)}
                onDateChange={(name, date) => handleDateChange(name, date)}
                inputProps={{
                  fullWidth: true,
                  id: "issue_date",
                  name: "issue_date",
                  label: intl.formatMessage({ id: "sharedScreen.issueDate" }),
                  placeholder: intl.formatMessage({
                    id: "sharedScreen.idIssueDate",
                  }),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IoIosCalendar />
                    </InputAdornment>
                  ),

                  value: formik.values.issue_date,
                  error:
                    Boolean(formik.touched.issue_date) &&
                    Boolean(formik.errors.issue_date),
                  helperText:
                    formik.touched.issue_date && formik.errors.issue_date,
                }}
                pickerProps={{
                  format: YYYY_MM_DD_FORMAT,
                  disabled: false,
                  showToday: false,
                }}
              />
            </Box>
            <Box width={24} />
            <Box className="inlineFormElement">
              <InputDate
                minDate={moment().format(YYYY_MM_DD_FORMAT)}
                onDateChange={(name, date) => handleDateChange(name, date)}
                inputProps={{
                  fullWidth: true,
                  id: "expiration_date",
                  name: "expiration_date",
                  label: intl.formatMessage({ id: "sharedScreen.expiryDate" }),
                  placeholder: intl.formatMessage({
                    id: "sharedScreen.enterExpiryDate",
                  }),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IoIosCalendar />
                    </InputAdornment>
                  ),

                  value: formik.values.expiration_date,
                  error:
                    Boolean(formik.touched.expiration_date) &&
                    Boolean(formik.errors.expiration_date),
                  helperText:
                    formik.touched.expiration_date &&
                    formik.errors.expiration_date,
                }}
                pickerProps={{
                  format: YYYY_MM_DD_FORMAT,
                  disabled: false,
                  showToday: false,
                }}
              />
            </Box>
          </Box>

          <Box component="div" mt={2} className={classes.subHeader}>
            <span>
              {" "}
              <FormattedMessage id="sharedScreen.idPhotos" />
            </span>
            <p>
              <FormattedMessage id="sharedScreen.tapToUpload" />
            </p>
          </Box>

          {idType === IdentificationTypeIdEnum.cni ? (
            <>
              <Box component="div">
                <ImageChooser
                  variant="square"
                  label={intl.formatMessage({
                    id: "sharedScreen.addFrontPhotoId",
                  })}
                  defaultFile={formik.values.cni_front}
                  onImageSelected={(file) =>
                    handleImageSelected("cni_front", file)
                  }
                />
                {formik.touched.cni_front && formik.errors.cni_front && (
                  <FormHelperText className="errorWrapper">
                    {formik.errors.cni_front}
                  </FormHelperText>
                )}
              </Box>
              <Box component="div" mt={2}>
                <ImageChooser
                  variant="square"
                  label={intl.formatMessage({
                    id: "sharedScreen.addBackPhotoId",
                  })}
                  defaultFile={formik.values.cni_back}
                  onImageSelected={(file) =>
                    handleImageSelected("cni_back", file)
                  }
                />
                {formik.touched.cni_back && formik.errors.cni_back && (
                  <FormHelperText className="errorWrapper">
                    {formik.errors.cni_back}
                  </FormHelperText>
                )}
              </Box>
            </>
          ) : (
            <Box component="div" mt={2}>
              <ImageChooser
                variant="square"
                label={intl.formatMessage({ id: "sharedScreen.addPassport" })}
                defaultFile={formik.values.passport}
                onImageSelected={(file) =>
                  handleImageSelected("passport", file)
                }
              />
              {formik.touched.passport && formik.errors.passport && (
                <FormHelperText className="errorWrapper">
                  {formik.errors.passport}
                </FormHelperText>
              )}
            </Box>
          )}

          <Box
            mt={10}
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
          >
            <span className="flatButton" onClick={(_) => backCallback()}>
              <FormattedMessage id="sharedScreen.back" />
            </span>
            <Box width={100}>
              <InputButton
                color="secondary"
                fullWidth
                size="medium"
                variant="contained"
                type="submit"
              >
                <FormattedMessage id="sharedScreen.next" />
              </InputButton>
            </Box>
          </Box>
        </form>
      </Box>

      <Overlay loading={formik.isSubmitting} />
    </>
  );
};
